import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";
import NotificationBell from "./NotificationBell";

const roleBadge = {
  employer: {
    key: "dashboard.role_badge_employer",
    className: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100",
  },
  agency: {
    key: "dashboard.role_badge_agency",
    className: "bg-teal-50 text-teal-700 ring-1 ring-teal-100",
  },
  admin: {
    key: "dashboard.role_badge_admin",
    className: "bg-amber-50 text-amber-700 ring-1 ring-amber-100",
  },
};

function navLinksFor(role) {
  const base = [{ to: "/dashboard", key: "nav.dashboard", testId: "nav-dashboard-link" }];
  if (role === "employer") {
    base.push({ to: "/missions/mine", key: "nav.my_missions", testId: "nav-missions-link" });
    base.push({ to: "/cvtheque", key: "nav.cvtheque", testId: "nav-cvtheque-link" });
    base.push({ to: "/messages", key: "nav.messages", testId: "nav-messages-link" });
    base.push({ to: "/settings/organization", key: "nav.settings", testId: "nav-settings-link" });
    base.push({ to: "/settings/account", key: "nav.account", testId: "nav-account-link" });
  } else if (role === "agency") {
    base.push({ to: "/marketplace", key: "nav.marketplace", testId: "nav-marketplace-link" });
    base.push({ to: "/missions/mine", key: "nav.my_missions", testId: "nav-missions-link" });
    base.push({ to: "/submissions/mine", key: "nav.my_submissions", testId: "nav-my-submissions-link" });
    base.push({ to: "/roster", key: "nav.roster", testId: "nav-roster-link" });
    base.push({ to: "/cvtheque", key: "nav.cvtheque", testId: "nav-cvtheque-link" });
    base.push({ to: "/messages", key: "nav.messages", testId: "nav-messages-link" });
    base.push({ to: "/settings/subscription", key: "nav.subscription", testId: "nav-subscription-link" });
    base.push({ to: "/settings/team", key: "nav.team", testId: "nav-team-link" });
    base.push({ to: "/settings/organization", key: "nav.settings", testId: "nav-settings-link" });
    base.push({ to: "/settings/account", key: "nav.account", testId: "nav-account-link" });
  } else if (role === "admin") {
    base.push({ to: "/admin/dashboard", key: "nav.admin_dashboard", testId: "nav-admin-dashboard-link" });
    base.push({ to: "/admin/approvals", key: "nav.admin_approvals", testId: "nav-admin-approvals-link" });
    base.push({ to: "/admin/missions", key: "nav.admin_missions", testId: "nav-admin-missions-link" });
    base.push({ to: "/admin/users", key: "nav.admin_users", testId: "nav-admin-users-link" });
    base.push({ to: "/admin/orgs", key: "nav.admin_orgs", testId: "nav-admin-orgs-link" });
    base.push({ to: "/admin/subscriptions", key: "nav.admin_subs", testId: "nav-admin-subs-link" });
    base.push({ to: "/admin/trials", key: "nav.admin_trials", testId: "nav-admin-trials-link" });
    base.push({ to: "/admin/audit", key: "nav.admin_audit", testId: "nav-admin-audit-link" });
    base.push({ to: "/settings/account", key: "nav.account", testId: "nav-account-link" });
  }
  return base;
}

export default function Header() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <header
      className="sticky top-0 z-30 w-full border-b border-slate-200 bg-white/85 backdrop-blur"
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-3">
        <Link
          to={user ? "/dashboard" : "/"}
          className="flex items-center gap-2 text-slate-900"
          data-testid="brand-link"
        >
          <img src="/Logo.png" alt="Talent Junction" className="h-9 w-9 rounded-lg object-cover" />
          <span className="text-base font-semibold tracking-tight">
            {t("brand")}
          </span>
        </Link>

        <div className="flex items-center gap-3">
          {user && (
            <nav className="hidden md:flex items-center gap-1">
              {navLinksFor(user.role).map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={l.testId}
                  end={l.to === "/dashboard"}
                  className={({ isActive }) =>
                    "rounded-lg px-3 py-1.5 text-sm font-medium transition-colors " +
                    (isActive
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-slate-700 hover:bg-slate-100")
                  }
                >
                  {t(l.key)}
                </NavLink>
              ))}
            </nav>
          )}

          {user && <NotificationBell />}

          {user ? (
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2">
                <span
                  className={
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide " +
                    roleBadge[user.role].className
                  }
                  data-testid="user-role-badge"
                >
                  {t(roleBadge[user.role].key)}
                </span>
                <span className="text-sm text-slate-700" data-testid="user-name">
                  {user.name}
                </span>
              </div>
              <button
                onClick={onLogout}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                data-testid="logout-btn"
              >
                {t("nav.logout")}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/login"
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
                data-testid="nav-login-link"
              >
                {t("nav.login")}
              </Link>
              <Link
                to="/signup/employer"
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
                data-testid="nav-signup-employer-link"
              >
                {t("nav.signup_employer")}
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
